import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { asyncConnect } from 'react-async-client';
import { Button } from 'antd';
import Translate from 'react-translate-component';
import { Link, withRouter } from 'react-router-dom';
import { path } from 'ramda';

import { requestMetroEdit } from 'constants/routes';
import { fullAutoOptions } from 'constants/asyncClient';
import { sidebarClose } from 'actions/sidebarActions';
import { getUser, getMetroRequest, postMetroRequestApproval } from 'actions/asyncActions';
import { openModalMetroRequestApprove, openModalMetroRequestReject, openModalMetroRequestCancel } from 'actions/modalActions';
import {
    METRO_REQUEST_INWORK,
    METRO_REQUEST_DRAFT,
    METRO_REQUEST_REJECTED,

    METRO_REQUEST_STORE,
    METRO_REQUEST_HQ,
    METRO_REQUEST_MGL,
    METRO_REQUEST_ROUTE_TYPES,
} from 'constants/metro';
import SidebarContainer from '../SidebarContainer';
import ApprovalButtons from 'components/common/controls/approvals/ApprovalButtons';
import { DefinitionHeader } from 'components/common/fields/DefinitionHeader';
import { DefinitionField } from 'components/common/fields/DefinitionField';
import DocumentList from 'components/common/fields/DocumentList';
import ApprovalComments from 'components/sidebars/approvals/ApprovalComments';
import ApprovalRouteList from 'components/views/metro/ApprovalRouteList';
import LoadField from 'components/common/fields/LoadField';
import TranslateField from 'components/common/fields/TranslateField';
import {
    metroTown,
    metroRegion,
    metroCell,
    metroCompanyCar,
    metroOccupationType,
    metroChartOfWork,
    yesnocombo
} from 'constants/dictionaries';

const stateToProps = state => ({
    user: getUser.selectData(state)
});

@asyncConnect({
    getMetroRequest: getMetroRequest
        .withPayload(({ id, params }) => ({ id, objtype: params.objtype }))
        .withOptions(fullAutoOptions),
    postMetroRequestApproval: postMetroRequestApproval
        .withSuccessHandler(({ params, sidebarClose, getMetroRequest: { refresh } }) => {
            if (params.approval) {
                sidebarClose();
            } else {
                refresh();
            }
        })
        .withErrorHandler(({ getMetroRequest: { refresh } }) => refresh()),
}, stateToProps, { sidebarClose, openModalMetroRequestApprove, openModalMetroRequestReject, openModalMetroRequestCancel })
@withRouter
export default class MetroRequestSidebar extends Component {
    static propTypes = {
        id: PropTypes.number.isRequired,
        params: PropTypes.object,
        user: PropTypes.object,
        getMetroRequest: PropTypes.object.isRequired,
        openModalMetroRequestApprove: PropTypes.func.isRequired,
        openModalMetroRequestReject: PropTypes.func.isRequired,
        openModalMetroRequestCancel: PropTypes.func.isRequired,
        history: PropTypes.object.isRequired
    };

    getUrl = () => (
        requestMetroEdit.stringify({ id: this.props.id })
    )

    getFooter = (request) => {
        const { params, user } = this.props;
        const draft = request.draft && (request.state === METRO_REQUEST_DRAFT);
        const onwRequest = request.initiatorCode === user.id;
        const isApproval = request.state === METRO_REQUEST_INWORK;
        const onApproval = (params.approval && !params.hideButtons) && isApproval;
        const rejected = !params.approval && (request.state === METRO_REQUEST_REJECTED);

        return (draft || rejected || onwRequest || onApproval) && <Fragment>
            { (draft || rejected || !onwRequest) && (
                <Fragment>
                    <Button onClick={() => this.props.history.push(this.getUrl())}>
                        <Translate content="events.edit" />
                    </Button>
                    { ((onwRequest && !isApproval) || draft) && (
                        <ApprovalButtons
                            selected={request}
                            filters={{ status: 'inwork' }}
                            cancel={() => this.props.openModalMetroRequestCancel([request])}
                        />
                    )}
                </Fragment>
            )}
            { onApproval && (
                <ApprovalButtons
                    selected={request}
                    approve={() => this.props.openModalMetroRequestApprove([request])}
                    reject={() => this.props.openModalMetroRequestReject([request])}
                />
            )}
        </Fragment>;
    }

    render() {
        const { params, getMetroRequest: { data, meta, refresh } } = this.props;

        const title = (
            <Link to={this.getUrl()}>
                <TranslateField
                    ru={data.position}
                    en={data.positionEngl}
                    defaultText={params.title}
                />
            </Link>
        );
        const footer = this.getFooter(data);
        const isNew = !data.cellId;
        const isApproval = !!params.approval;
        const isStore = data.routeType === METRO_REQUEST_STORE;
        const isHQ = data.routeType === METRO_REQUEST_HQ;
        const isMGL = data.routeType === METRO_REQUEST_MGL;

        return (
            <SidebarContainer meta={meta} refresh={refresh} title={title} footer={footer}>
                {meta.success && (
                    <Fragment>
                        <DefinitionHeader translate="metro.request.structure" iconType='info-outline' />
                        <div className='panel-view'>
                            <DefinitionField translate="metro.request.requestRoute">
                                <Translate content={`metro.request.routes.${METRO_REQUEST_ROUTE_TYPES[data.routeType]}`} />
                            </DefinitionField>
                            <DefinitionField translate="metro.request.region" show={data.region}>
                                <LoadField fieldValue='value' fieldName='name' loadName={metroRegion} fieldId={data.region} />
                            </DefinitionField>
                            <DefinitionField translate="metro.request.city">
                                <LoadField fieldValue='value' fieldName='name' loadName={metroTown} fieldId={data.city} />
                            </DefinitionField>
                            <DefinitionField translate="metro.request.storeCode" text={data.storeCode} />
                            <DefinitionField translate="metro.request.directorateEngl" text={data.directorateEngl} />
                            <DefinitionField translate="metro.request.unitEngl" text={data.directorateEngl ? data.unitEngl || <Translate content='metro.request.noUnit' /> : null} />
                            <DefinitionField translate="metro.request.divisionEngl" text={data.divisionEngl} />
                            <DefinitionField translate="metro.request.department" show={data.departmentCode}>
                                <TranslateField en={data.departmentEngl} ru={data.department} />
                            </DefinitionField>
                            <DefinitionField showAlways translate="metro.request.functionCode" text={data.function} />
                            <DefinitionField translate="metro.request.initiatorName">
                                <TranslateField en={data.initiatorEngl} ru={data.initiator} />
                            </DefinitionField>
                            <DefinitionField translate="metro.request.positionCode">
                                { !isNew ? (
                                    <LoadField
                                        fieldValue='id'
                                        fieldName='name'
                                        loadName={metroCell}
                                        loadParams={{ DepartmentID: data.departmentCode }}
                                        fieldId={data.cellId}
                                        renderItem={({ item = {} }) => item.name ? (
                                            <span>
                                                {item.name} - <Translate
                                                    content={`metro.request.position${item.is_vacant ? 'Vacant' : 'Placed'}`}
                                                /> {item.worker_name && (
                                                    <span>({item.worker_name})</span>
                                                )}
                                            </span>
                                        ) : null}
                                    />
                                ) : (
                                    <Translate content='metro.request.positionNew' />
                                )}
                            </DefinitionField>
                            { !isNew ? (
                                data.displacedEmployeeCode ? (
                                    <Fragment>
                                        <DefinitionField translate="metro.request.positionPlace">
                                            <Translate content='metro.request.positionDisplaced' />
                                        </DefinitionField>
                                        <DefinitionField translate="metro.request.displacedEmployee">
                                            <TranslateField en={data.displacedEmployeeEngl} ru={data.displacedEmployee} />
                                        </DefinitionField>
                                    </Fragment>
                                ) : (
                                    <DefinitionField translate="metro.request.positionPlace">
                                        <Translate content='metro.request.positionVacanted' />
                                    </DefinitionField>
                                )
                            ) : null }
                        </div>
                        <DefinitionHeader translate="metro.position" iconType='vacancy' />
                        <div className='panel-view'>
                            <DefinitionField showAlways translate="metro.request.position" text={data.position} />
                            <DefinitionField showAlways translate="metro.request.positionEngl" text={data.positionEngl} />
                            <DefinitionField showAlways translate="metro.request.initiatorName">
                                <TranslateField en={data.initiatorEngl} ru={data.initiator} />
                            </DefinitionField>
                            <DefinitionField showAlways translate="metro.request.chief">
                                <TranslateField en={data.chiefEngl} ru={data.chief} />
                            </DefinitionField>
                            <DefinitionField translate="metro.request.chiefPosition">
                                <TranslateField en={data.chiefPositionEngl} ru={data.chiefPosition} />
                            </DefinitionField>
                            <DefinitionField showAlways translate="metro.request.occupationType">
                                <LoadField fieldName='name' loadName={metroOccupationType} fieldId={data.occupationTypeCode} />
                            </DefinitionField>
                            <DefinitionField showAlways translate="metro.request.chartOfWork">
                                <LoadField fieldName='name' loadName={metroChartOfWork} fieldId={data.chartOfWorkCode} />
                            </DefinitionField>
                            <DefinitionField translate="metro.request.displacedEmployee">
                                <TranslateField en={data.displacedEmployeeEngl} ru={data.displacedEmployee} />
                            </DefinitionField>
                            <DefinitionField showAlways translate="metro.request.contractPeriod" text={data.contractPeriod} />
                            <DefinitionField showAlways translate="metro.request.mobilePhone">
                                <LoadField fieldName='name' loadName={yesnocombo} fieldId={data.mobilePhoneCode} />
                            </DefinitionField>
                            <DefinitionField translate={data.routeType === METRO_REQUEST_STORE ? 'metro.request.HRM' : 'metro.request.HQHRGeneralist'}>
                                <TranslateField en={data.HRGeneralistEngl} ru={data.HRGeneralist} />
                            </DefinitionField>
                            <DefinitionField translate="metro.request.HQHRBP">
                                <TranslateField en={data.HRBPEngl} ru={data.HRBP} />
                            </DefinitionField>
                            <DefinitionField translate="metro.request.recruiter">
                                <TranslateField en={data.recruiterEngl} ru={data.recruiter} />
                            </DefinitionField>
                            { isStore && (
                                <DefinitionField showAlways translate="metro.request.HRBPOperations">
                                    <TranslateField en={data.HRBPOperationsEngl} ru={data.HRBPOperations} />
                                </DefinitionField>
                            )}
                            <DefinitionField showAlways translate="metro.request.responsibilities" text={!!data.responsibilities && <div className='text-pre-space'>{data.responsibilities}</div>} />
                            <DefinitionField showAlways translate="metro.request.requirements" text={!!data.requirements && <div className='text-pre-space'>{data.requirements}</div>} />
                            <DefinitionField showAlways translate="metro.request.positionCostCenters" text={data.positionCostCenters} />
                            { isHQ && (
                                <DefinitionField showAlways translate="metro.request.HQ" text={data.HQ} />
                            )}
                            <DefinitionField showAlways translate="metro.request.positionGrade" text={data.positionGrade} />
                            <DefinitionField showAlways translate="metro.request.positionLevel" text={data.positionLevel} />
                            <DefinitionField showAlways translate="metro.request.bonusScheme" text={data.bonusScheme} />
                            <DefinitionField showAlways translate="metro.request.familyDMS">
                                <LoadField fieldName='name' loadName={yesnocombo} fieldId={data.familyDMSCode} />
                            </DefinitionField>
                            <DefinitionField showAlways translate="metro.request.companyCar">
                                <LoadField fieldName='name' loadName={metroCompanyCar} fieldId={data.companyCarCode} />
                            </DefinitionField>
                            { isStore && (
                                <Fragment>
                                    <DefinitionField showAlways translate="metro.request.accessToPC">
                                        <LoadField fieldName='name' loadName={yesnocombo} fieldId={data.accessToPCCode} />
                                    </DefinitionField>
                                </Fragment>
                            )}
                            { (isMGL || isNew || isApproval) && (
                                <Fragment>
                                    <DefinitionField showAlways translate="metro.request.travellingWork">
                                        <LoadField fieldName='name' loadName={yesnocombo} fieldId={data.travellingWorkCode} />
                                    </DefinitionField>
                                    <DefinitionField showAlways translate="metro.request.liabilityContract">
                                        <LoadField fieldName='name' loadName={yesnocombo} fieldId={data.liabilityContractCode} />
                                    </DefinitionField>
                                </Fragment>
                            )}
                            { isApproval && (
                                <DefinitionField showAlways translate="metro.request.positionBudgeted">
                                    <LoadField fieldName='name' loadName={yesnocombo} fieldId={data.positionBudgetedCode} />
                                </DefinitionField>
                            )}
                            <DefinitionField translate="metro.request.approval" text={data.approval} />
                            <DefinitionField translate="metro.request.positionIsInCatalog" text={data.positionIsInCatalog} />
                            <DefinitionField translate="metro.request.positionIsInOrgstr" text={data.positionIsInOrgstr} />
                            <DefinitionField translate="metro.request.positionIsInSAP" text={data.positionIsInSAP} />
                        </div>
                        { data.competences ? (
                            <Fragment>
                                <DefinitionHeader translate="metro.request.competences" iconType='tags' />
                                <div className='panel-view'>
                                    { data.competences.map((item, index) => (
                                        <div className='text-pre-space m-b-15' key={index}>
                                            <b>{path(['label'], item)}</b>
                                            {path(['name'], item)}
                                        </div>
                                    ))}
                                </div>
                            </Fragment>
                        ) : null }
                        { data.documents ? (
                            <Fragment>
                                <DefinitionHeader translate="metro.request.documents" iconType='file' />
                                <DocumentList documents={data.documents} metro={data.id} />
                            </Fragment>
                        ) : null }
                        <ApprovalComments comments={data.comments} />
                        { data.approvalRoute ? (
                            <Fragment>
                                <DefinitionHeader translate="metro.approvalRoute" iconType='tags' />
                                <div className='panel-view'>
                                    <ApprovalRouteList approvalRoute={data.approvalRoute} sidebar={true} />
                                </div>
                            </Fragment>
                        ) : null }
                        { data.approvalHistory ? (
                            <Fragment>
                                <DefinitionHeader translate="metro.approvalHistory" iconType='case' />
                                <div className='panel-view'>
                                    { data.approvalHistory.map((route, index) => (
                                        <div className='text-pre-space m-b-15' key={index}>
                                            <b>{path(['approver', 'name'], route)}</b> &mdash; <span>{path(['result'], route)}</span>
                                            <div>{path(['comment'], route)}</div>
                                        </div>
                                    ))}
                                </div>
                            </Fragment>
                        ) : null }
                    </Fragment>
                )}
            </SidebarContainer>
        );
    }
}
